import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { FaBuilding, FaCheck, FaChalkboardTeacher, FaGraduationCap, FaShieldAlt } from 'react-icons/fa';
import Header from '../components/header';
import Footers from '../components/footers';
import { serverURL } from '../constants';
import {
    authHeaders,
    getSessionAuth,
    persistLoginSession,
    resolveHomePath,
} from '../shared/auth/session';
import { useOrgAuthUi } from '../shared/branding/AuthOrgBrand';

function slugify(value) {
    return String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 48);
}

/** Single organization plan — everything included for now. */
const ORG_PLAN = 'organization';

const ORG_INCLUDED_KEYS = [
    'includedBranded',
    'includedInvite',
    'includedBranding',
    'includedDomain',
    'includedSso',
];

/**
 * Self-serve organization workspace setup — schools & companies.
 */
export default function OrganizationStart() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const authUi = useOrgAuthUi();
    const session = getSessionAuth();
    const loggedIn = Boolean(session.auth);

    const [enabled, setEnabled] = useState(true);
    const [rootDomain, setRootDomain] = useState('coursolo.ai');
    const [busy, setBusy] = useState(false);
    const [slugStatus, setSlugStatus] = useState({ checking: false, available: null });
    const [form, setForm] = useState({
        name: '',
        slug: '',
        mName: '',
        email: session.email || '',
        password: '',
    });

    useEffect(() => {
        if (authUi.isTenantHost && authUi.organization?.tenant) {
            // Already on a tenant host — joining, not creating
            return;
        }
        axios.get(`${serverURL}/api/organizations/self-serve`)
            .then(({ data }) => {
                if (data?.rootDomain) setRootDomain(data.rootDomain);
                if (data?.enabled === false) setEnabled(false);
            })
            .catch(() => {});
    }, [authUi.isTenantHost, authUi.organization?.tenant]);

    useEffect(() => {
        const slug = form.slug;
        if (!slug || slug.length < 2) {
            setSlugStatus({ checking: false, available: null });
            return undefined;
        }
        setSlugStatus({ checking: true, available: null });
        const timer = setTimeout(async () => {
            try {
                const { data } = await axios.get(`${serverURL}/api/organizations/slug-available`, {
                    params: { slug },
                });
                setSlugStatus({ checking: false, available: Boolean(data?.available) });
            } catch {
                setSlugStatus({ checking: false, available: null });
            }
        }, 350);
        return () => clearTimeout(timer);
    }, [form.slug]);

    const hostPreview = useMemo(
        () => (form.slug
            ? `${form.slug}.${rootDomain}`
            : t('auth.organizationStart.hostPreviewFallback', { rootDomain })),
        [form.slug, rootDomain, t]
    );

    const onNameChange = (name) => {
        setForm((prev) => {
            const next = { ...prev, name };
            if (!prev.slug || prev.slug === slugify(prev.name)) {
                next.slug = slugify(name);
            }
            return next;
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.slug.trim()) {
            toast.error(t('auth.organizationStart.toastNameUrlRequired'));
            return;
        }
        if (!loggedIn && (!form.mName.trim() || !form.email.trim() || form.password.length < 9)) {
            toast.error(t('auth.organizationStart.toastAdminFieldsRequired'));
            return;
        }
        setBusy(true);
        try {
            const payload = {
                name: form.name.trim(),
                slug: form.slug.trim(),
                plan: ORG_PLAN,
            };
            if (!loggedIn) {
                payload.mName = form.mName.trim();
                payload.email = form.email.trim().toLowerCase();
                payload.password = form.password;
            }
            const { data } = await axios.post(
                `${serverURL}/api/organizations/start`,
                payload,
                { headers: loggedIn ? authHeaders() : { 'Content-Type': 'application/json' } }
            );
            if (!data?.success) throw new Error(data?.message || t('auth.organizationStart.toastCreateFailed'));

            persistLoginSession(data.userData || {}, data.uploadLimits, {
                accessToken: data.accessToken || data.token,
                refreshToken: data.refreshToken,
                organizationId: data.organizationId,
                orgRole: data.orgRole || 'org_admin',
                orgSlug: data.orgSlug || data.organization?.slug,
                platformAdmin: data.platformAdmin,
                role: data.userData?.role || 'teacher',
            });

            toast.success(t('auth.organizationStart.toastWorkspaceReady'));
            window.location.href = resolveHomePath(getSessionAuth());
        } catch (err) {
            const code = err.response?.data?.code;
            const msg = err.response?.data?.message || err.message;
            if (code === 'EMAIL_EXISTS') {
                toast.error(msg);
                navigate('/signin', { state: { from: '/organizations/start' } });
            } else if (code === 'ALREADY_ORG_ADMIN') {
                toast.info(msg);
                navigate('/org/admin');
            } else {
                toast.error(msg || t('auth.organizationStart.toastCreateFailed'));
            }
        } finally {
            setBusy(false);
        }
    };

    if (authUi.organization?.tenant) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
                <Header />
                <main className="max-w-xl mx-auto px-4 pt-28 pb-16 text-center">
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                        {t('auth.organizationStart.tenantTitle')}
                    </h1>
                    <p className="mt-3 text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                        {t('auth.organizationStart.tenantBody', { company: authUi.companyName })}
                    </p>
                    <div className="mt-6 flex flex-wrap justify-center gap-3">
                        <Link to="/signup" className="px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold">
                            {t('auth.organizationStart.joinCompany', { company: authUi.companyName })}
                        </Link>
                        <Link to="/signin" className="px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-700 dark:text-slate-200">
                            {t('auth.organizationStart.signIn')}
                        </Link>
                    </div>
                </main>
                <Footers />
            </div>
        );
    }

    const bullets = [
        { icon: FaBuilding, titleKey: 'bulletWorkspaceTitle', desc: t('auth.organizationStart.bulletWorkspaceDesc', { host: hostPreview }) },
        { icon: FaShieldAlt, titleKey: 'bulletAdminTitle', desc: t('auth.organizationStart.bulletAdminDesc') },
        { icon: FaChalkboardTeacher, titleKey: 'bulletInstructorsTitle', desc: t('auth.organizationStart.bulletInstructorsDesc') },
        { icon: FaGraduationCap, titleKey: 'bulletLearnersTitle', desc: t('auth.organizationStart.bulletLearnersDesc') },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-indigo-50/40 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900">
            <Header />
            <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-16">
                <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-8 lg:gap-10 items-start">
                    <section className="space-y-6">
                        <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
                            {t('auth.organizationStart.eyebrow')}
                        </p>
                        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight">
                            {t('auth.organizationStart.heroTitle')}
                        </h1>
                        <p className="text-slate-600 dark:text-slate-300 text-sm sm:text-base leading-relaxed max-w-xl">
                            {t('auth.organizationStart.heroBodyBefore')}{' '}
                            <strong>{t('auth.organizationStart.organizationWord')}</strong>{' '}
                            {t('auth.organizationStart.heroBodyAfter')}
                        </p>

                        <ul className="space-y-3">
                            {bullets.map(({ icon: Icon, titleKey, desc }) => (
                                <li key={titleKey} className="flex gap-3">
                                    <span className="mt-0.5 w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-300 flex items-center justify-center shrink-0">
                                        <Icon size={14} />
                                    </span>
                                    <div>
                                        <p className="text-sm font-semibold text-slate-900 dark:text-white">
                                            {t(`auth.organizationStart.${titleKey}`)}
                                        </p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{desc}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/60 p-4 text-xs text-slate-600 dark:text-slate-300 space-y-2">
                            <p className="font-semibold text-slate-800 dark:text-slate-100">
                                {t('auth.organizationStart.notSureTitle')}
                            </p>
                            <p>
                                <strong>{t('auth.organizationStart.pathPersonalLabel')}</strong>{' '}
                                {t('auth.organizationStart.pathPersonalOn')}{' '}
                                <Link className="text-indigo-600 underline" to="/signup">
                                    {t('auth.organizationStart.signUp')}
                                </Link>
                                {' '}{t('auth.organizationStart.pathPersonalRest')}
                            </p>
                            <p>
                                <strong>{t('auth.organizationStart.pathOrgLabel')}</strong>{' '}
                                {t('auth.organizationStart.pathOrgRest')}
                            </p>
                            <p>
                                <strong>{t('auth.organizationStart.pathSsoLabel')}</strong>{' '}
                                {t('auth.organizationStart.pathSsoRest')}
                            </p>
                        </div>
                    </section>

                    <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm p-5 sm:p-6">
                        {!enabled ? (
                            <div className="space-y-3 text-sm">
                                <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                                    {t('auth.organizationStart.contactTitle')}
                                </h2>
                                <p className="text-slate-600 dark:text-slate-300">
                                    {t('auth.organizationStart.contactBody')}
                                </p>
                                <Link to="/contact" className="inline-flex px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold">
                                    {t('auth.organizationStart.contactSales')}
                                </Link>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                                        {t('auth.organizationStart.formTitle')}
                                    </h2>
                                    <p className="text-xs text-slate-500 mt-1">
                                        {t('auth.organizationStart.formSubtitle')}
                                    </p>
                                </div>

                                <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/40 px-3.5 py-3">
                                    <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 mb-2">
                                        {t('auth.organizationStart.includedHeading')}
                                    </p>
                                    <ul className="space-y-1.5">
                                        {ORG_INCLUDED_KEYS.map((key) => (
                                            <li key={key} className="flex gap-2 text-[11px] text-slate-600 dark:text-slate-300">
                                                <FaCheck size={10} className="mt-0.5 text-emerald-500 shrink-0" />
                                                <span>{t(`auth.organizationStart.${key}`)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <label className="block space-y-1.5">
                                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                                        {t('auth.organizationStart.fieldOrgName')}
                                    </span>
                                    <input
                                        required
                                        value={form.name}
                                        onChange={(e) => onNameChange(e.target.value)}
                                        placeholder={t('auth.organizationStart.placeholderOrgName')}
                                        className="w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-950 px-3 py-2.5 text-sm"
                                    />
                                </label>

                                <label className="block space-y-1.5">
                                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                                        {t('auth.organizationStart.fieldWorkspaceUrl')}
                                    </span>
                                    <div className="flex items-center gap-1 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-950 px-3 py-2">
                                        <input
                                            required
                                            value={form.slug}
                                            onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                                            pattern="[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?"
                                            className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                                            placeholder={t('auth.organizationStart.placeholderSlug')}
                                        />
                                        <span className="text-xs text-slate-400 shrink-0">.{rootDomain}</span>
                                    </div>
                                    <p className="text-[11px] text-slate-500">
                                        {slugStatus.checking && t('auth.organizationStart.slugChecking')}
                                        {!slugStatus.checking && slugStatus.available === true && (
                                            <span className="text-emerald-600 inline-flex items-center gap-1">
                                                <FaCheck size={10} /> {t('auth.organizationStart.slugAvailable')}
                                            </span>
                                        )}
                                        {!slugStatus.checking && slugStatus.available === false && (
                                            <span className="text-amber-600">
                                                {t('auth.organizationStart.slugUnavailable')}
                                            </span>
                                        )}
                                    </p>
                                </label>

                                {!loggedIn ? (
                                    <>
                                        <div className="pt-1 border-t border-slate-100 dark:border-slate-800">
                                            <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-2">
                                                {t('auth.organizationStart.adminAccountHeading')}
                                            </p>
                                        </div>
                                        <label className="block space-y-1.5">
                                            <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                                                {t('auth.organizationStart.fieldYourName')}
                                            </span>
                                            <input
                                                required
                                                value={form.mName}
                                                onChange={(e) => setForm({ ...form, mName: e.target.value })}
                                                className="w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-950 px-3 py-2.5 text-sm"
                                            />
                                        </label>
                                        <label className="block space-y-1.5">
                                            <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                                                {t('auth.organizationStart.fieldWorkEmail')}
                                            </span>
                                            <input
                                                required
                                                type="email"
                                                value={form.email}
                                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                                className="w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-950 px-3 py-2.5 text-sm"
                                            />
                                        </label>
                                        <label className="block space-y-1.5">
                                            <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                                                {t('auth.organizationStart.fieldPassword')}
                                            </span>
                                            <input
                                                required
                                                type="password"
                                                minLength={9}
                                                value={form.password}
                                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                                className="w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-950 px-3 py-2.5 text-sm"
                                            />
                                        </label>
                                    </>
                                ) : (
                                    <p className="text-xs rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 px-3 py-2 text-slate-600 dark:text-slate-300">
                                        {t('auth.organizationStart.signedInAsAdmin', { email: session.email })}
                                    </p>
                                )}

                                <button
                                    type="submit"
                                    disabled={busy || slugStatus.available === false}
                                    className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50"
                                >
                                    {busy
                                        ? t('auth.organizationStart.creating')
                                        : t('auth.organizationStart.createSubmit')}
                                </button>

                                <p className="text-[11px] text-center text-slate-500">
                                    {t('auth.organizationStart.personalAccountHint')}{' '}
                                    <Link to="/signup" className="text-indigo-600 font-medium underline">
                                        {t('auth.organizationStart.signUp')}
                                    </Link>
                                </p>
                            </form>
                        )}
                    </section>
                </div>
            </main>
            <Footers />
        </div>
    );
}
